/**
 * HugeIcons
 *
 * This file defines:
 *  - A collection of HugeIcons imports
 *  - Mapped Vuetify aliases (`huge:*`)
 *  - A Vuetify-compatible wrapper component for rendering HugeIcons
 *
 * Why the `huge:` prefix?
 *   To avoid collisions with Vuetify's built-in MDI aliases and any other
 *   custom icons that may also be registered.
 *
 * Usage examples:
 *   <v-icon icon="huge:barCode" />
 *   <v-icon icon="huge:packageSearch" />
 *
 * Adding a new icon from hugeIcons directly:
 *   1. Import the icon from any HugeIcons package (@hugeicons-pro/...)
 *   2. Add it to `hugeIconCollection` using a clear, kebab-case key
 *   3. The alias will automatically become "huge:<your-key>"
 *
 *  Adding a custom icon:
 *   If you need to add a custom icon, you will need to upload this to hugeicons.com
 *   and re-generate the hgi-bettermile.js asset file, then import the new icon.
 */
import { h } from 'vue';

import type { IconProps } from 'vuetify';

import { HugeiconsIcon } from '@hugeicons/vue';
// Vuetify Icon Mapping
import {
  ArrowDown01Icon as ChevronDown,
  ArrowLeft01Icon as ChevronLeft,
  ArrowRight01Icon as ChevronRight,
  ArrowUp01Icon as ChevronUp,
  Backward01Icon as BackwardSolidIcon, // temp solution
  CancelCircleIcon as CancelCircleSolidIcon,
  CancelSquareIcon,
  CheckmarkCircle02Icon as CheckmarkCircleSolidIcon,
  CircleIcon as CircleSolidIcon,
  DarkModeIcon,
  Forward01Icon as ForwardSolidIcon, // temp solution
  Gps01Icon as Gps01SolidIcon,
  KeyframesDoubleRemoveIcon as KeyframesDoubleRemoveSolidIcon,
  LoginMethodIcon as LoginMethodSolidIcon,
  MinusSignCircleIcon as MinusSignCircleSolidIcon,
  PauseIcon as PauseSolidIcon,
  PlayIcon as PlaySolidIcon,
  RefreshCwOffIcon as RefreshCwOffSolidIcon,
  ReplayIcon as ReplaySolidIcon,
  RouteOffIcon as RouteOffSolidIcon,
  StarHalfIcon,
  StarIcon as StarFullIcon,
  TimeManagementIcon as TimeManagementSolidIcon
} from '@hugeicons-pro/core-solid-standard';
// Core Stroke Rounded
import {
  BarCode02Icon,
  PackageAddIcon,
  PackageIcon,
  PackageMovingIcon,
  PackageRemoveIcon,
  PackageSearchIcon,
  SquarePenIcon,
  ThumbsDownIcon,
  ThumbsUpIcon
} from '@hugeicons-pro/core-stroke-rounded';
import {
  // Better Design Icons
  Alert02Icon,
  AlertCircleIcon,
  AlertDiamondIcon,
  ApiIcon,
  ArrowDown01Icon,
  ArrowDown02Icon,
  ArrowLeft01Icon,
  ArrowLeft02Icon,
  ArrowLeftDoubleIcon,
  // Icons needed for Vuetify Mapping
  ArrowMoveDownLeftIcon,
  ArrowRight01Icon,
  ArrowRight02Icon,
  ArrowRightDoubleIcon,
  ArrowUp01Icon,
  ArrowUp02Icon,
  ArrowUp03Icon,
  Attachment02Icon,
  BalanceScaleIcon,
  Bicycle01Icon,
  BinocularsIcon,
  // Used in Design Guide
  BorderNone01Icon,
  BoxesIcon,
  BubbleChatQuestionIcon,
  Calendar03Icon,
  Cancel01Icon,
  CancelCircleIcon,
  Chart01Icon,
  Chart03Icon,
  ChartDownIcon,
  ChartUpIcon,
  CheckmarkCircle02Icon,
  CircleIcon,
  ClockHourFourIcon,
  CloudAngledRainIcon,
  CloudAngledRainZapIcon,
  CloudAngledZapIcon,
  CloudBigRainIcon,
  CloudFastWindIcon,
  CloudHailstoneIcon,
  CloudIcon,
  CloudLittleRainIcon,
  CloudLittleSnowIcon,
  CloudMidRainIcon,
  CloudMidSnowIcon,
  CloudSnowIcon,
  CodeIcon,
  ColorPickerIcon,
  CommandIcon,
  ComputerPhoneSyncIcon,
  Copy01Icon,
  Csv01Icon,
  CursorMagicSelection03Icon,
  CustomizeIcon,
  DashboardSquare02Icon,
  Database01Icon,
  Delete02Icon,
  DeliveryTracking01Icon,
  Download01Icon,
  FastWindIcon,
  FileDownloadIcon,
  FileExportIcon,
  FileUploadIcon,
  FilterIcon,
  FilterRemoveIcon,
  FolderDetailsIcon,
  FullScreenIcon,
  Globe02Icon,
  Gps01Icon,
  GridViewIcon,
  HealthIcon,
  HistoryIcon,
  HoldPhoneIcon,
  Home09Icon,
  Image03Icon,
  InformationCircleIcon,
  InsertBottomImageIcon,
  Key01Icon,
  KnightShieldIcon,
  LayoutTable01Icon,
  LayoutThreeColumnIcon,
  LeftToRightListBulletIcon,
  LeftToRightListTriangleIcon,
  LinkSquare02Icon,
  LiveStreaming02Icon,
  Loading03Icon,
  Location06Icon,
  LocationCheck02Icon,
  LocationOffline03Icon,
  LoginMethodIcon,
  LowSignalIcon,
  LoyaltyCardIcon,
  MailAdd01Icon,
  MapsIcon,
  MapsOffIcon,
  MaximizeScreenIcon,
  Menu01Icon,
  MenuCollapseIcon,
  Message01Icon,
  MessageQuestionIcon,
  MinusSignIcon,
  Moon02Icon,
  MoreHorizontalIcon,
  MoreVerticalIcon,
  Navigation03Icon,
  Notification01Icon,
  OfficeIcon,
  OptionIcon,
  PackageProcessIcon,
  PaintBoardIcon,
  PauseIcon,
  PayByCheckIcon,
  PencilEdit01Icon,
  PieChart01Icon,
  PinCodeIcon,
  PinLocation01Icon,
  PlayIcon,
  PlaySquareIcon,
  PlusSignIcon,
  PreferenceHorizontalIcon,
  PresentationBarChart01Icon,
  PuzzleIcon,
  Quiz03Icon,
  RefreshCwOffIcon,
  ResizeFieldRectangleIcon,
  Route01Icon,
  Route02Icon,
  RouteBlockIcon,
  RouteOffIcon,
  Search02Icon,
  SearchList01Icon,
  ServerStack01Icon,
  Settings01Icon,
  Settings05Icon,
  ShieldUserIcon,
  ShippingLoadingIcon,
  SnowIcon,
  SolidLine01Icon,
  SparklesIcon,
  SquareArrowExpand01Icon,
  SquareLock02Icon,
  SquareLockCheck01Icon,
  StarIcon,
  Sun03Icon,
  SunCloud01Icon,
  SunCloud02Icon,
  SunCloudAngledRain02Icon,
  SunCloudAngledRainZap02Icon,
  SunCloudAngledZap02Icon,
  SunCloudBigRain02Icon,
  SunCloudLittleRain02Icon,
  SunCloudLittleSnow02Icon,
  Tablet02Icon,
  TaskEdit02Icon,
  TextFontIcon,
  TextSquareIcon,
  Tick02Icon,
  TickDouble02Icon,
  TimelineIcon,
  TimeManagementIcon,
  Timer01Icon,
  TouchInteraction01Icon,
  TransactionHistoryIcon,
  // Icons used for Quick-Sight
  Trolley02Icon,
  TruckDeliveryIcon,
  UnavailableIcon,
  UnfoldMoreIcon,
  Upload01Icon,
  User03Icon,
  UserAccountIcon,
  UserAdd01Icon,
  UserCheck01Icon,
  UserCircle02Icon,
  UserGroup03Icon,
  UserIdVerificationIcon,
  UserListIcon,
  UserRemove01Icon,
  UserSettings01Icon,
  UserShield01Icon,
  UserSwitchIcon,
  ViewIcon,
  ViewOffIcon,
  VolumeHighIcon,
  VolumeLowIcon,
  VolumeMute01Icon,
  VolumeOffIcon,
  Xls01Icon,
  ZapIcon
} from '@hugeicons-pro/core-stroke-standard';
import { kebabCase } from 'lodash';

import type { IconSvgObject } from '@/types/theme';
// Automatically import all custom icons
import * as CustomHugeIcons from './hgi-bettermile';

type HugeBaseName = keyof typeof hugeIconCollection;
type HugePrefixedName = `huge:${Extract<HugeBaseName, string>}`;
type ActualIconProps = IconProps & { size?: string | number };

// Map the imports to the collection object
const mappedCustomIcons = Object.entries(CustomHugeIcons).reduce(
  (acc, [name, icon]) => {
    acc[kebabCase(name)] = icon as IconSvgObject;
    return acc;
  },
  {} as Record<string, IconSvgObject>
);

/**
 * Icon collection:
 *   - Keys = the shorthand alias (without prefix)
 *   - Values = HugeIcons component
 *
 * Example:
 *   bar-code → huge:bar-code
 */
const hugeIconCollection = {
  ...mappedCustomIcons,
  // Stroke Rounded
  'bar-code': BarCode02Icon,
  package: PackageIcon,
  'package-add': PackageAddIcon,
  'package-moving': PackageMovingIcon,
  'package-remove': PackageRemoveIcon,
  'package-search': PackageSearchIcon,
  'square-pen': SquarePenIcon,

  // Stroke Standard
  alert: Alert02Icon,
  'alert-circle': AlertCircleIcon,
  'alert-diamond': AlertDiamondIcon,
  api: ApiIcon,
  'arrow-down': ArrowDown01Icon,
  'arrow-down-02': ArrowDown02Icon,
  'arrow-left': ArrowLeft01Icon,
  'arrow-left-02': ArrowLeft02Icon,
  'arrow-left-double': ArrowLeftDoubleIcon,
  'arrow-right': ArrowRight01Icon,
  'arrow-right-02': ArrowRight02Icon,
  'arrow-right-double': ArrowRightDoubleIcon,
  'arrow-up': ArrowUp01Icon,
  'arrow-up-02': ArrowUp02Icon,
  'balance-scale': BalanceScaleIcon,
  bicycle: Bicycle01Icon,
  binoculars: BinocularsIcon,
  boxes: BoxesIcon,
  'bubble-chat-question': BubbleChatQuestionIcon,
  calendar: Calendar03Icon,
  cancel: Cancel01Icon,
  chart: Chart01Icon,
  'chart-03': Chart03Icon,
  'chart-down': ChartDownIcon,
  'chart-up': ChartUpIcon,
  'checkmark-circle': CheckmarkCircle02Icon,
  'checkmark-circle-solid': CheckmarkCircleSolidIcon,
  circle: CircleIcon,
  'clock-hour-4': ClockHourFourIcon,
  'circle-solid': CircleSolidIcon,
  cloud: CloudIcon,
  code: CodeIcon,
  copy: Copy01Icon,
  'crosshairs-solid': Gps01SolidIcon,
  csv: Csv01Icon,
  'dark-mode': DarkModeIcon,
  'dashboard-square': DashboardSquare02Icon,
  database: Database01Icon,
  delete: Delete02Icon,
  'delivery-tracking': DeliveryTracking01Icon,
  download: Download01Icon,
  'forward-solid': ForwardSolidIcon,
  'file-download': FileDownloadIcon,
  'file-upload': FileUploadIcon,
  filter: FilterIcon,
  'filter-remove': FilterRemoveIcon,
  'folder-details': FolderDetailsIcon,
  globe: Globe02Icon,
  gps: Gps01Icon,
  health: HealthIcon,
  history: HistoryIcon,
  'hold-phone': HoldPhoneIcon,
  home: Home09Icon,
  'information-circle': InformationCircleIcon,
  key: Key01Icon,
  'keyframes-double-remove-solid': KeyframesDoubleRemoveSolidIcon,
  'knight-shield': KnightShieldIcon,
  'layout-3-column': LayoutThreeColumnIcon,
  'layout-table': LayoutTable01Icon,
  'left-to-right-list-bullet': LeftToRightListBulletIcon,
  'left-to-right-list-triangle': LeftToRightListTriangleIcon,
  'link-square': LinkSquare02Icon,
  'live-streaming': LiveStreaming02Icon,
  loading: Loading03Icon,
  location: Location06Icon,
  'location-check': LocationCheck02Icon,
  'location-offline': LocationOffline03Icon,
  'login-method': LoginMethodIcon,
  'login-method-solid': LoginMethodSolidIcon,
  'low-signal': LowSignalIcon,
  'mail-add': MailAdd01Icon,
  maps: MapsIcon,
  'maps-off': MapsOffIcon,
  menu: Menu01Icon,
  'menu-collapse': MenuCollapseIcon,
  'message-question': MessageQuestionIcon,
  'minus-sign': MinusSignIcon,
  'minus-sign-circle-solid': MinusSignCircleSolidIcon,
  moon: Moon02Icon,
  'more-horizontal': MoreHorizontalIcon,
  'more-vertical': MoreVerticalIcon,
  navigation: Navigation03Icon,
  notification: Notification01Icon,
  office: OfficeIcon,
  'package-process': PackageProcessIcon,
  'pause-solid': PauseSolidIcon,
  'pay-by-check': PayByCheckIcon,
  'pencil-edit': PencilEdit01Icon,
  'pie-chart': PieChart01Icon,
  'pin-code': PinCodeIcon,
  'pin-location': PinLocation01Icon,
  'play-solid': PlaySolidIcon,
  'play-square': PlaySquareIcon,
  plus: PlusSignIcon,
  'preference-horizontal': PreferenceHorizontalIcon,
  'presentation-bar-chart': PresentationBarChart01Icon,
  quiz03: Quiz03Icon,
  'refresh-cw-off': RefreshCwOffIcon,
  'refresh-cw-off-solid': RefreshCwOffSolidIcon,
  'restart-solid': ReplaySolidIcon,
  'backward-solid': BackwardSolidIcon,
  route: Route01Icon,
  'route-02': Route02Icon,
  'route-block': RouteBlockIcon,
  'route-off': RouteOffIcon,
  'route-off-solid': RouteOffSolidIcon,
  search: Search02Icon,
  'search-list': SearchList01Icon,
  'server-stack': ServerStack01Icon,
  settings: Settings01Icon,
  'settings-05': Settings05Icon,
  'shield-user': ShieldUserIcon,
  'shipping-loading': ShippingLoadingIcon,
  sparkles: SparklesIcon,
  'square-lock': SquareLock02Icon,
  'square-lock-check': SquareLockCheck01Icon,
  star: StarIcon,
  'task-edit': TaskEdit02Icon,
  tick: Tick02Icon,
  'tick-double': TickDouble02Icon,
  'transaction-history': TransactionHistoryIcon,
  'truck-delivery': TruckDeliveryIcon,
  unavailable: UnavailableIcon,
  upload: Upload01Icon,
  user: User03Icon,
  'user-account': UserAccountIcon,
  'user-add': UserAdd01Icon,
  'user-check': UserCheck01Icon,
  'user-circle': UserCircle02Icon,
  'user-group': UserGroup03Icon,
  'user-id-verification': UserIdVerificationIcon,
  'user-list': UserListIcon,
  'user-remove': UserRemove01Icon,
  'user-settings': UserSettings01Icon,
  'user-shield': UserShield01Icon,
  'user-switch': UserSwitchIcon,
  view: ViewIcon,
  'view-off': ViewOffIcon,
  xls: Xls01Icon,

  // Weather Icons
  'cloud-angled-rain-zap': CloudAngledRainZapIcon,
  'cloud-angled-rain': CloudAngledRainIcon,
  'cloud-angled-zap': CloudAngledZapIcon,
  'cloud-big-rain': CloudBigRainIcon,
  'cloud-fast-wind': CloudFastWindIcon,
  'cloud-hailstone': CloudHailstoneIcon,
  'cloud-little-rain': CloudLittleRainIcon,
  'cloud-little-snow': CloudLittleSnowIcon,
  'cloud-mid-rain': CloudMidRainIcon,
  'cloud-mid-snow': CloudMidSnowIcon,
  'cloud-snow': CloudSnowIcon,
  'fast-wind': FastWindIcon,
  'sun-03': Sun03Icon,
  'sun-cloud-01': SunCloud01Icon,
  'sun-cloud-02': SunCloud02Icon,
  'sun-cloud-angled-rain-02': SunCloudAngledRain02Icon,
  'sun-cloud-angled-rain-zap-02': SunCloudAngledRainZap02Icon,
  'sun-cloud-angled-zap-02': SunCloudAngledZap02Icon,
  'sun-cloud-big-rain-02': SunCloudBigRain02Icon,
  'sun-cloud-little-rain-02': SunCloudLittleRain02Icon,
  'sun-cloud-little-snow-02': SunCloudLittleSnow02Icon,
  snow: SnowIcon,
  zap: ZapIcon,

  // Icons needed for Vuetify Mapping
  option: OptionIcon,
  backspace: CancelSquareIcon,
  'cancel-circle': CancelCircleIcon,
  'cancel-circle-solid': CancelCircleSolidIcon,
  color: PaintBoardIcon,
  command: CommandIcon,
  'arrow-move-down-left': ArrowMoveDownLeftIcon,
  'color-picker': ColorPickerIcon,
  'attachment-02': Attachment02Icon,
  'full-screen': FullScreenIcon,
  'full-screen-exit': MaximizeScreenIcon,
  pause: PauseIcon,
  play: PlayIcon,
  'rating-full': StarFullIcon,
  'rating-half': StarHalfIcon,
  'arrow-up-03': ArrowUp03Icon,
  'solid-line-01': SolidLine01Icon,
  'chevron-down': ChevronDown,
  'chevron-up': ChevronUp,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  'unfold-more': UnfoldMoreIcon,
  'volume-high': VolumeHighIcon,
  'volume-low': VolumeLowIcon,
  'volume-mute': VolumeMute01Icon,
  'volume-off': VolumeOffIcon,

  // Used in the design library
  'border-none-01': BorderNone01Icon,
  'computer-phone-sync': ComputerPhoneSyncIcon,
  'file-export': FileExportIcon,
  'grid-view': GridViewIcon,
  'image-03': Image03Icon,
  'insert-bottom-image': InsertBottomImageIcon,
  'loyalty-card': LoyaltyCardIcon,
  'resize-field-rectangle': ResizeFieldRectangleIcon,
  'square-arrow-expand': SquareArrowExpand01Icon,
  'tablet-02': Tablet02Icon,
  'text-font': TextFontIcon,
  'text-square': TextSquareIcon,
  'thumbs-up': ThumbsUpIcon,
  'thumbs-down': ThumbsDownIcon,
  customize: CustomizeIcon,
  message: Message01Icon,
  puzzle: PuzzleIcon,

  // Icons used for Quick-Sight
  trolly: Trolley02Icon,
  'cursor-magic-selection': CursorMagicSelection03Icon,
  'time-management': TimeManagementIcon,
  'time-management-solid': TimeManagementSolidIcon,
  timeline: TimelineIcon,
  'timer-01': Timer01Icon,
  'touch-interaction-01': TouchInteraction01Icon
} as const;

/**
 * Build prefixed aliases:
 *   huge:bar-code → BarCode02Icon
 */
const hugeAliases = Object.fromEntries(
  Object.entries(hugeIconCollection).map(([name, component]) => [
    `huge:${name}`,
    component
  ])
) as Record<HugePrefixedName, IconSvgObject>;

/**
 * Vuetify wrapper for HugeIcons.
 *
 * This wrapper allows HugeIcons to be used seamlessly inside Vuetify’s
 * <v-icon> system while keeping consistent sizing and stroke widths.
 *
 * What it does:
 *   - Resolves HugeIcons via aliases (with `huge:`)
 *   - Applies consistent default props:
 *       size = 24
 *   - Supports passing an icon component directly
 *   Stroke width is set to HugeIcons' default of 1.5
 *
 * Props expected (from Vuetify):
 *   @param {string|Component} props.icon  Alias or HugeIcons component
 *   @param {number} [props.size=24]       Icon size
 *   @param {...any} props.*               Additional props passed to HugeiconsIcon
 *
 * Examples:
 *   <v-icon icon="huge:packageSearch" />
 *   <v-icon :icon="MyCustomIcon" :size="32" />
 */
const hugeIconComponentWrapper = (props: IconProps) => {
  // 2. Cast props to our extended type so we can destructure 'size'
  const { icon: rawIcon, size = 24, ...rest } = props as ActualIconProps;

  let finalIcon: any = rawIcon;

  if (typeof finalIcon === 'string') {
    const lookupKey = (
      finalIcon.startsWith('huge:') ? finalIcon : `huge:${finalIcon}`
    ) as HugePrefixedName;

    const resolvedIcon = hugeAliases[lookupKey];

    if (!resolvedIcon) {
      console.warn(`[HugeIcons] Icon "${finalIcon}" not found.`);
      return null;
    }

    finalIcon = resolvedIcon;
  }

  // 3. We use 'finalIcon as any' here because the HugeiconsIcon component
  // expects the [tag, attrs][] format, which conflicts with Vuetify's internal types.
  return h(HugeiconsIcon, {
    icon: finalIcon as any,
    size,
    ...rest
  });
};

export const hugeIcons = {
  set: {
    component: hugeIconComponentWrapper
  },
  aliases: hugeAliases as any
};

/**
 * Sorted List for UI
 *
 * Generate list of all icons in the Huge Icon list,
 * and sort it in alphabetical order
 */
export const hugeIconList = (
  Object.keys(hugeAliases) as HugePrefixedName[]
).sort((a, b) =>
  a.replace(/^huge:/, '').localeCompare(b.replace(/^huge:/, ''))
);
